import React, {
  useState,
  useEffect,
  type MutableRefObject,
  useRef,
} from 'react';
import { OnChangeFunction, Option } from '../..';
import DropdownList from '../DropdownList';
import useClickOutside from '~/hooks/useClickOutside';
import { isNil } from 'ramda';
import useKeyBindings from './hooks/useKeyBindings';
import PortalledPositioner from '~/components/molecule/PortalledPositioner';
import type { RootId } from '~/components/molecule/Portal';

export type Props = {
  /** The options to display in the dropdown list */
  options: Array<Option>;

  /** The index of the selected option */
  selectedOptionIdx?: number | null;

  /** Whether the dropdown list is open */
  dropdownListOpen: boolean;

  /** Select the first option by default */
  withDefaultFirstOption?: boolean;

  /** Loading state shown in the dropdown list */
  loading?: boolean;
  dropdownWidth?: number;

  /** Index of the option to scroll to when dropdown opens */
  indexToDisplayOnOpen?: number;

  /** Goes to the container */
  className?: string;

  /**
   * The component that opened the dropdown list. A button or a dropdown etc.
   */
  openerRef: MutableRefObject<HTMLElement | null>;

  /** The root id of the portalled positioner */
  positionerRootId?: RootId;

  /** Called when option is selected with keybindings or option click */
  onChange: OnChangeFunction;

  /** Called on escape key press */
  onClose?: () => void;

  /** Called when click is outside the list */
  onClickOutside?: (e?: MouseEvent | TouchEvent) => void;
};

const DropdownListContainer: React.FCC<Props> = ({
  dataTestId,
  options,
  withDefaultFirstOption,
  loading,
  dropdownWidth,
  indexToDisplayOnOpen,
  openerRef,
  positionerRootId,
  dropdownListOpen,
  onChange,
  onClose,
  onClickOutside,
  ...rest
}) => {
  const selected =
    rest.selectedOptionIdx && rest.selectedOptionIdx >= 0
      ? rest.selectedOptionIdx
      : withDefaultFirstOption
        ? 0
        : null;

  const [selectedOptionIdx, setSelectedOptionIdx] = useState<number | null>(
    selected,
  );

  const listRef = useRef<HTMLUListElement>(null);

  // Scroll to the specified index when the dropdown opens
  useEffect(() => {
    if (
      dropdownListOpen &&
      listRef.current &&
      !isNil(indexToDisplayOnOpen) &&
      indexToDisplayOnOpen >= 0 &&
      indexToDisplayOnOpen < options.length
    ) {
      // Use setTimeout to ensure the list is fully rendered
      setTimeout(() => {
        const listElement = listRef.current;
        if (!listElement) return;

        // Find the option element to scroll to
        const optionElements = listElement.querySelectorAll(
          '[data-option-index], li',
        );
        const targetOption = optionElements[
          indexToDisplayOnOpen
        ] as HTMLElement;

        if (targetOption) {
          // Calculate the scroll position to center the target option
          const listHeight = listElement.clientHeight;
          const optionHeight = targetOption.offsetHeight;
          const optionTop = targetOption.offsetTop;

          // Center the option in the visible area
          const scrollTop = optionTop - listHeight / 2 + optionHeight / 2;

          listElement.scrollTop = Math.max(0, scrollTop);
        }
      }, 0);
    }
  }, [indexToDisplayOnOpen, options.length, listRef, dropdownListOpen]);

  useClickOutside({
    callback: dropdownListOpen && onClickOutside ? onClickOutside : () => {},
    allowClickInRefs: [listRef, openerRef],
  });

  useKeyBindings({
    onClose,
    onSelect: optionIdx => setSelectedOptionIdx(optionIdx),
    selectedOptionIdx,
    options,
    onChange,
    enabled: dropdownListOpen,
  });

  return (
    <>
      <PortalledPositioner
        rootId={positionerRootId || 'dropdown-portal'}
        openerRef={openerRef}
        dataTestId={dataTestId}
        isOpen={dropdownListOpen}
      >
        <DropdownList
          dropdownWidth={dropdownWidth || 0}
          loading={loading}
          options={options}
          onChange={onChange}
          actuallySelected={rest.selectedOptionIdx}
          selectedOptionIdx={selectedOptionIdx}
          onClickOutside={onClickOutside}
          ref={listRef}
        />
      </PortalledPositioner>
    </>
  );
};

export default DropdownListContainer;
